"use client";

import React from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { PropertyService, PropertyLeadType } from "@/services";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { buildActionId, startUserAction } from "@/utils/idempotency";
import { withUtmParams } from "@/utils/share-links";

export interface PropertyLeadContext {
  leadUserId?: string | null;
  isGuestUser?: boolean;
  realtorId?: string | null;
  propertyCreatedById?: string | null;
  leadUserRole?: string | null;
}

export interface PropertyContactContext {
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  price?: number;
  realtor_id?: string;
  developer_id?: string;
}

interface WhatsAppButtonProps {
  phone: string;
  propertyId?: string;
  lead?: PropertyLeadContext;
  propertyContext?: PropertyContactContext;
  messagePrefix?: string;
  className?: string;
  buttonText?: string;
}

export const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({
  phone,
  propertyId,
  lead,
  propertyContext,
  messagePrefix = "Hello, I would like to inquire about your property on FindNigeriaProperties.com.",
  className = "w-full h-11 bg-[#19545e] hover:bg-[#13424a] text-white text-sm font-semibold rounded-xl transition-colors shadow-xs",
  buttonText = "Contact On WhatsApp",
}) => {
  const { trackWhatsAppClicked } = usePostHogAnalytics();

  const handleWhatsAppClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!phone) return;

    // One key per user action: the insight counter and the lead row of this
    // click share it, and a duplicate delivery of the same click reuses it
    // instead of creating a second lead. `startUserAction` returns null when
    // this action is already in flight, so the duplicate is skipped entirely
    // (no second WhatsApp tab).
    const idempotencyKey = startUserAction(
      buildActionId("contact", "whatsapp", propertyId),
    );
    if (!idempotencyKey) {
      console.debug(
        "[WhatsAppButton] duplicate click skipped",
        { propertyId, action: "contact:whatsapp" },
      );
      return;
    }

    console.debug("[WhatsAppButton] click started", {
      propertyId,
      action: "contact:whatsapp",
      idempotencyKey,
    });

    if (propertyId) {
      console.debug("[WhatsAppButton] tracking insight", {
        propertyId,
        action: "whatsapp_contact",
        idempotencyKey,
        t: Date.now(),
      });
      PropertyService.TrackPropertyInsight(propertyId, "whatsapp_contact", {
        idempotencyKey,
      });
      console.debug("[WhatsAppButton] tracking lead", {
        propertyId,
        type: PropertyLeadType.WHATSAPP_CLICK,
        idempotencyKey,
        t: Date.now(),
      });
      /*      
      PropertyService.TrackPropertyLead(
        {
          propertyId,
          type: PropertyLeadType.WHATSAPP_CLICK,
          leadUserId: lead?.leadUserId ?? null,
          isGuestUser: lead?.isGuestUser ?? true,
          realtorId: lead?.realtorId ?? null,
          propertyCreatedById: lead?.propertyCreatedById ?? null,
          leadUserRole: lead?.leadUserRole ?? null,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
          ipAddress: null,
        },
        { idempotencyKey },
      );
      */
      trackWhatsAppClicked({
        property_id: propertyId,
        listing_type: propertyContext?.listing_type,
        property_type: propertyContext?.property_type,
        location_city: propertyContext?.location_city,
        price: propertyContext?.price,
        realtor_id: propertyContext?.realtor_id ?? lead?.realtorId ?? undefined,
        developer_id: propertyContext?.developer_id,
      });
    }

    const rawPhone = phone.trim();
    const parsed = parsePhoneNumberFromString(rawPhone, "NG");

    const formattedNumber = parsed?.isValid()
      ? parsed.number.replace("+", "")
      : rawPhone.replace(/\D/g, "").replace(/^0+/, "234");

    const propertyUrl =
      typeof window !== "undefined"
        ? withUtmParams(window.location.href, "whatsapp", "property_listing", propertyId)
        : "";
    const message = encodeURIComponent(`${messagePrefix} ${propertyUrl}`.trim());
    const whatsappUrl = `https://wa.me/${formattedNumber}?text=${message}`;

    console.debug("[WhatsAppButton] opening whatsapp url", {
      whatsappUrl,
      t: Date.now(),
    });

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      type="button"
      className={`!flex !flex-row items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-semibold rounded-xl transition-colors shadow-xs ${className}`}
      onClick={handleWhatsAppClick}
    >
      <svg
        className="w-5 h-5 fill-current text-white shrink-0"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
      </svg>
      <span className="leading-none">{buttonText}</span>
    </button>
  );
};